import {
  BillingInformation,
  useUpdateBillingAddressDetails,
  useUpdateBillingInformationProvider,
  useBillingInformationRemoteAddresses,
} from '@app/api';
import {
  SubmitButton,
  Button,
  TextLoadingPlaceholder,
  useModalState,
} from '@app/core';
import { Theme } from '@app/theme';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { BillingInformationForm, SubmitButtonsLine } from 'components';
import classNames from 'classnames';
import { billingInformationToString } from './billingInformationToString';

interface Props {
  billingInformation: BillingInformation;
  onConfirm?: (billingInformation: BillingInformation) => void;
}

const useStyles = createUseStyles((theme: Theme) => ({
  title: {
    ...theme.fonts.h4,
    marginBottom: theme.marginBase * 2,
  },
  options: {
    display: 'flex',
    flexDirection: 'column',
  },
  option: {
    borderRadius: theme.borderRadius.std,
    padding: theme.marginBase,
    border: {
      style: 'solid',
      width: 1,
      color: theme.colors.grayBorder,
    },
    cursor: 'pointer',

    transition:
      'opacity .3s, max-height .5s, padding .5s, margin .5s, border-width .5s',
    opacity: 1,
    maxHeight: 200,
    marginBottom: theme.marginBase * 2,
    overflow: 'hidden',
  },
  selectedOption: {
    borderColor: theme.colors.green,
    backgroundColor: theme.colors.greenMuted,
  },
  disabledOption: {
    opacity: 0.4,
  },
  action: {
    ...theme.fonts.label,
  },
  address: {
    ...theme.fonts.caption,
    whiteSpace: 'pre-line',
  },
  hiddenOption: {
    opacity: 1,
    maxHeight: 0,
    paddingTop: 0,
    paddingBottom: 0,
    borderTopWidth: 0,
    borderBottomWidth: 0,
    marginBottom: 0,
    pointerEvents: 'none',
  },
  selectionButtons: {
    marginTop: theme.marginBase * 2,
  },
  editionButtons: {
    marginTop: theme.marginBase * 4,
  },
  '@keyframes formAppear': {
    from: {
      opacity: 0,
      transform: `translateY(-${theme.marginBase}px)`,
      maxHeight: 0,
    },
    to: {
      opacity: 1,
      transform: 'none',
      maxHeight: 500,
    },
  },
  form: {
    animation: '$formAppear .7s ease',
    overflow: 'hidden',
  },
}));

const copyAddress = (to: BillingInformation, from: BillingInformation) => ({
  ...to,
  email: from.email,
  billingAddress: from.billingAddress,
  providerAddressId: from.providerAddressId,
});

export const AddressSelectionStep = ({
  billingInformation,
  onConfirm,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { mutate, isLoading: isSaving } = useUpdateBillingAddressDetails(
    billingInformation.id,
  );
  const { mutate: mutateProvider } = useUpdateBillingInformationProvider(
    billingInformation.id,
  );

  const { data: addresses = [], isLoading } =
    useBillingInformationRemoteAddresses(billingInformation);

  const [selection, setSelection] = useState<BillingInformation | null>(null);
  const [editing, edit, stopEditing] = useModalState<BillingInformation | null>(
    null,
  );

  const creationDisabled =
    isLoading ||
    addresses.some(
      (i) =>
        billingInformationToString(i, true) ===
        billingInformationToString(billingInformation, true),
    );
  const creationSelected = !!selection && !selection.providerAddressId;

  const confirm = () => {
    if (!selection) {
      return;
    }
    mutate(copyAddress(billingInformation, selection), {
      onSuccess: () =>
        mutateProvider(
          {
            provider: billingInformation.provider!,
            providerId: billingInformation.providerId,
            providerAddressId: selection.providerAddressId ?? null,
          },
          { onSuccess: onConfirm },
        ),
    });
  };

  return (
    <div>
      <h2 className={classes.title}>
        {t('admin.billing.billingInfo.addressSelection.title')}
      </h2>
      <div className={classes.options}>
        {isLoading && (
          <div className={classes.option}>
            <div className={classes.address}>
              <TextLoadingPlaceholder sample="4 bis boulevard de Ventadour" />
              <br />
              <TextLoadingPlaceholder sample="4 bis boulevard de Ventadour" />
              <br />
              <TextLoadingPlaceholder sample="4 bis boulevard de Ventadour" />
            </div>
          </div>
        )}

        {addresses.map((info) => (
          <div
            key={info.providerAddressId}
            className={classNames(classes.option, {
              [classes.selectedOption]:
                info.providerAddressId === selection?.providerAddressId,
              [classes.hiddenOption]:
                editing &&
                info.providerAddressId !== selection!.providerAddressId,
            })}
            onClick={() => setSelection(info)}
            aria-selected={
              info.providerAddressId === selection?.providerAddressId
            }
            role="option"
          >
            <div className={classes.address}>
              {billingInformationToString(info, true)}
            </div>
          </div>
        ))}
        <div
          className={classNames(classes.option, {
            [classes.selectedOption]: creationSelected,
            [classes.disabledOption]: creationDisabled,
            [classes.hiddenOption]: !creationSelected && editing,
          })}
          onClick={() =>
            creationDisabled
              ? null
              : setSelection({
                  ...billingInformation,
                  providerAddressId: undefined,
                })
          }
          aria-disabled={creationDisabled}
          aria-selected={creationSelected}
          role="option"
        >
          <div className={classes.action}>
            {t('admin.billing.billingInfo.addressSelection.createNew')}
          </div>
          <div className={classes.address}>
            {billingInformationToString(billingInformation, true)}
          </div>
        </div>
      </div>
      {!editing && (
        <SubmitButtonsLine className={classes.selectionButtons}>
          <Button
            onClick={() => edit(copyAddress(billingInformation, selection!))}
            disabled={!selection}
            small
            text={
              creationSelected
                ? t('admin.billing.billingInfo.addressSelection.createAndEdit')
                : t('admin.billing.billingInfo.addressSelection.useAndEdit')
            }
            color="green"
          />
          <SubmitButton
            onClick={confirm}
            disabled={!selection}
            small
            loading={isSaving}
            text={
              creationSelected
                ? t('admin.billing.billingInfo.addressSelection.create')
                : t('admin.billing.billingInfo.addressSelection.use')
            }
          />
        </SubmitButtonsLine>
      )}

      {!!editing && selection && (
        <div className={classes.form}>
          <BillingInformationForm
            billingInformation={editing}
            onSuccess={() =>
              mutateProvider(
                {
                  provider: billingInformation.provider!,
                  providerId: billingInformation.providerId,
                  providerAddressId: editing.providerAddressId ?? null,
                },
                { onSuccess: onConfirm },
              )
            }
            addressOnly
            submitButton={
              <SubmitButtonsLine className={classes.editionButtons}>
                <Button
                  color="ink"
                  text={t('admin.billing.billingInfo.cancelEdition')}
                  onClick={stopEditing}
                  type="button"
                  variant="muted"
                  small
                />
                <SubmitButton
                  text={t('admin.billing.billingInfo.edit')}
                  small
                />
              </SubmitButtonsLine>
            }
          />
        </div>
      )}
    </div>
  );
};
